import React, { useState, useRef, useEffect } from "react";
import { XMarkIcon, ArrowDownTrayIcon, CheckIcon, ChevronDownIcon } from "@heroicons/react/24/outline";
import classes from "./downloadTemplateModal.module.css";
import { useGetDownloadTemplateOptionsQuery } from "../../../services/bulk/downloadTemplateApiSlice";

/* ─────────────────────────────────────────
   Custom Select Component
───────────────────────────────────────── */
const CustomSelect = ({ options, value, onChange, placeholder, disabled }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  const selected = options.find((o) => o.id === value) ?? null;

  /* Close on outside click */
  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const handleSelect = (option) => {
    onChange(option.id);
    setOpen(false);
  };

  return (
    <div
      ref={ref}
      className={`${classes.customSelect} ${disabled ? classes["customSelect--disabled"] : ""} ${open ? classes["customSelect--open"] : ""}`}
    >
      {/* Trigger */}
      <button
        type="button"
        className={classes.customSelect__trigger}
        onClick={() => !disabled && setOpen((o) => !o)}
        disabled={disabled}
      >
        <span className={selected ? classes["customSelect__value--selected"] : classes.customSelect__placeholder}>
          {selected ? selected.label : placeholder}
        </span>
        <ChevronDownIcon
          width={14}
          height={14}
          className={`${classes.customSelect__chevron} ${open ? classes["customSelect__chevron--open"] : ""}`}
        />
      </button>

      {/* Dropdown */}
      {open && (
        <ul className={classes.customSelect__menu}>
          {options.map((option) => (
            <li
              key={option.id}
              className={`${classes.customSelect__option} ${option.id === value ? classes["customSelect__option--active"] : ""}`}
              onClick={() => handleSelect(option)}
            >
              {option.label}
              {option.id === value && <CheckIcon width={12} height={12} className={classes.customSelect__optionCheck} />}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

/* ─────────────────────────────────────────
   Modal
───────────────────────────────────────── */
export const DownloadTemplateModal = ({ onClose }) => {
  const { data: options = [], isLoading } = useGetDownloadTemplateOptionsQuery();

  const [selectedMainCategory, setSelectedMainCategory] = useState(null);
  const [selectedCategory, setSelectedCategory]         = useState(null);
  const [selectedSubCat, setSelectedSubCat]             = useState(null);

  const categories    = selectedMainCategory?.categories ?? [];
  const subCategories = selectedCategory?.sub_categories ?? [];
  const downloadUrl   = selectedSubCat?.template_file_url ?? null;

  const steps = [
    { label: "Main Category", done: !!selectedMainCategory },
    { label: "Category",      done: !!selectedCategory },
    { label: "Sub-category",  done: !!selectedSubCat },
  ];

  const handleMainCategory = (id) => {
    const item = options.find((o) => o.id === id) ?? null;
    setSelectedMainCategory(item);
    setSelectedCategory(null);
    setSelectedSubCat(null);
  };

  const handleCategory = (id) => {
    const item = categories.find((c) => c.id === id) ?? null;
    setSelectedCategory(item);
    setSelectedSubCat(null);
  };

  const handleSubCat = (id) => {
    const item = subCategories.find((s) => s.id === id) ?? null;
    setSelectedSubCat(item);
  };

  const handleDownload = () => {
    if (!downloadUrl) return;
    const link = document.createElement("a");
    link.href = downloadUrl;
    link.setAttribute("download", "template.xlsx");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    onClose();
  };

  return (
    <div className={classes.overlay} onClick={onClose}>
      <div className={classes.modal} onClick={(e) => e.stopPropagation()}>

        {/* Header */}
        <div className={classes.modal__header}>
          <h2 className={classes.modal__title}>Download Template</h2>
          <button className={classes.modal__close} onClick={onClose}>
            <XMarkIcon width={18} height={18} />
          </button>
        </div>

        {/* Stepper */}
        <div className={classes.stepper}>
          {steps.map((step, i) => (
            <React.Fragment key={step.label}>
              <div className={classes.stepper__step}>
                <div className={`${classes.stepper__circle} ${step.done ? classes["stepper__circle--done"] : ""}`}>
                  {step.done ? <CheckIcon width={12} height={12} /> : i + 1}
                </div>
                <span className={`${classes.stepper__label} ${step.done ? classes["stepper__label--done"] : ""}`}>
                  {step.label}
                </span>
              </div>
              {i < steps.length - 1 && (
                <div className={`${classes.stepper__line} ${steps[i].done ? classes["stepper__line--done"] : ""}`} />
              )}
            </React.Fragment>
          ))}
        </div>

        {/* Dropdowns */}
        {isLoading ? (
          <p className={classes.modal__loading}>Loading options...</p>
        ) : (
          <div className={classes.modal__body}>

            {/* Step 1 — Main Category */}
            <div className={classes.field}>
              <label className={classes.field__label}>Main Category <span>*</span></label>
              <CustomSelect
                options={options}
                value={selectedMainCategory?.id ?? null}
                onChange={handleMainCategory}
                placeholder="Select main category"
              />
            </div>

            {/* Step 2 — Category */}
            <div className={classes.field}>
              <label className={`${classes.field__label} ${!selectedMainCategory ? classes["field__label--disabled"] : ""}`}>
                Category <span>*</span>
              </label>
              <CustomSelect
                options={categories}
                value={selectedCategory?.id ?? null}
                onChange={handleCategory}
                placeholder="Select category"
                disabled={!selectedMainCategory}
              />
            </div>

            {/* Step 3 — Sub-category */}
            <div className={classes.field}>
              <label className={`${classes.field__label} ${!selectedCategory ? classes["field__label--disabled"] : ""}`}>
                Sub-category <span>*</span>
              </label>
              <CustomSelect
                options={subCategories}
                value={selectedSubCat?.id ?? null}
                onChange={handleSubCat}
                placeholder="Select sub-category"
                disabled={!selectedCategory}
              />
            </div>

          </div>
        )}

        {/* Footer */}
        <div className={classes.modal__footer}>
          <button className={classes.btn__cancel} onClick={onClose}>Cancel</button>
          <button
            className={classes.btn__download}
            disabled={!downloadUrl}
            onClick={handleDownload}
          >
            <ArrowDownTrayIcon width={15} height={15} />
            Download
          </button>
        </div>

      </div>
    </div>
  );
};